import { connectToServer } from "./net";
import { createRemoteGame } from "./remote-game";
import { createOwnWormController } from "./prediction";

export interface WigglePeerOptions {
  room?: string;
  serverOverride?: string | null;
  name?: string;
  colorId?: string;
  amplitude?: number; // peak steering, 0..1 (fractional, like a touch player)
  periodSec?: number; // seconds per full left-right wobble
  lagMs?: number; // simulated one-way network lag on its outbound updates
  jitterMs?: number; // random +/- variation added to lagMs (bad-wifi jitter)
  jumps?: boolean; // leave holes like a real worm (default false: a clean test trail)
  // Send like the real client (RemoteGame): its path every 33ms instead of every 16ms
  // tick, and its steering only when it changes, in steps of 1/steerSteps (keys give
  // -1/0/1). Off by default; the perf bench turns it on so the server sees real load.
  clientCadence?: boolean;
  steerSteps?: number;
}

// A throwaway, self-driving TEST opponent: a second client-authoritative connection
// that joins the SAME room as you and steers in a slow left/right wobble, exactly
// like a real person playing on their own device. It predicts its own path and
// streams worm_segments just like the real client, so it exercises the true remote
// human render path (which a server-side bot does NOT: bots are simulated server
// side and steer at full tilt). Use it to test how opponents render without needing
// a second person. Returns stop() to disconnect and clean up.
//
// lagMs/jitterMs hold back its OUTBOUND messages (steering + path), so its trail
// reaches the server (and therefore your screen) late and jittery, the way a player
// on a worse connection would. Set it to mimic a phone on bad wifi.
export function createWigglePeer(opts: WigglePeerOptions = {}): () => void {
  const {
    room,
    serverOverride,
    name = "Practice",
    colorId = "blue",
    amplitude = 0.5,
    periodSec = 1.6,
    lagMs = 0,
    jitterMs = 0,
    jumps = false,
    clientCadence = false,
    steerSteps = 4,
  } = opts;

  const socket = connectToServer(serverOverride, room);
  // No render delay (the peer renders nothing), except with clientCadence: then the
  // real client's default, which a switcharoonie hand-off relies on (the worm taken
  // over is a render-delayed copy).
  const remote = clientCadence ? createRemoteGame() : createRemoteGame(0);
  let myId: string | null = null;
  let predictor: ReturnType<typeof createOwnWormController> | null = null;
  let loop: ReturnType<typeof setInterval> | null = null;
  let t0 = Date.now();
  let tick = 0;
  let lastSteering: number | null = null;
  const pending = new Set<ReturnType<typeof setTimeout>>();
  // Release time of the last queued message, so jitter never lets a later message
  // overtake an earlier one. Real bad wifi delays packets but keeps them in order
  // (socket.io is over TCP); reordering would scramble the adopted path on the
  // server, which no real client ever does.
  let lastReleaseAt = 0;

  // Emit with the simulated outbound lag (plus jitter) so the peer's updates land
  // late, like a player on a poor connection, but always IN ORDER. Zero lag emits
  // immediately.
  function laggedEmit(event: string, payload: any) {
    if (lagMs <= 0 && jitterMs <= 0) {
      socket.emit(event, payload);
      return;
    }
    const now = Date.now();
    const base = now + Math.max(0, lagMs + (Math.random() * 2 - 1) * jitterMs);
    const releaseAt = Math.max(base, lastReleaseAt + 1); // monotonic: keep order
    lastReleaseAt = releaseAt;
    const id = setTimeout(() => {
      pending.delete(id);
      socket.emit(event, payload);
    }, releaseAt - now);
    pending.add(id);
  }

  function stopLoop() {
    if (loop) {
      clearInterval(loop);
      loop = null;
    }
  }
  function startLoop() {
    stopLoop();
    loop = setInterval(() => {
      if (!predictor || !myId) return;
      const gs = remote.getGameState();
      if (!gs) return;
      // Gentle fractional left/right wobble, the kind of analog/touch steering that
      // makes a human's path differ from a hard-steering AI bot.
      let s = amplitude * Math.sin(((Date.now() - t0) / 1000) * ((2 * Math.PI) / periodSec));
      if (clientCadence) s = Math.round(s * steerSteps) / steerSteps;
      predictor.setSteering(s);
      if (!clientCadence || s !== lastSteering) laggedEmit("player_steering", { playerId: myId, steering: s });
      lastSteering = s;
      predictor.predict(gs, remote.getServerTime());
      if (clientCadence && ++tick % 2 !== 0) return; // the real client sends every 33ms
      const newWorms = predictor.drainNewWorms ? predictor.drainNewWorms() : [];
      for (const worm of newWorms) laggedEmit("worm_spawn", { playerId: myId, worm });
      const segments = predictor.drainSegments(gs);
      if (segments.length) laggedEmit("worm_segments", { playerId: myId, segments });
    }, 16);
  }

  socket.on("connect", () => {
    socket.emit("play", { name, colorId, localPlayers: [{ name, colorId }], room, wire: 3 });
  });
  socket.on("assigned", (m: any) => {
    myId = (m.playerIds && m.playerIds[0]) || m.playerId || null;
    if (myId) {
      predictor = createOwnWormController(myId, { disableJumps: !jumps });
      remote.setLocalPlayerId(myId);
    }
  });
  socket.on("game_start", (m: any) => {
    remote.setInitial(m.gameState);
    if (myId) remote.setLocalPlayerId(myId);
    if (predictor) predictor.reset(remote.getGameState());
    t0 = Date.now();
    startLoop();
  });
  socket.on("game_update", (m: any) => remote.applyUpdate(m.data));
  // Switcharoonie, as RemoteGame does it: without these the peer keeps driving its old
  // worm after the swap, the host drops those segments, and its new worm goes silent.
  socket.on("switch_freeze", (m: any) => { if (m && predictor && predictor.switchFreeze) predictor.switchFreeze(m.atTime, m.assignments); });
  socket.on("switch_go", (m: any) => { if (m && predictor && predictor.switchGo) predictor.switchGo(m.atTime); });
  socket.on("game_over", stopLoop);
  socket.on("match_over", stopLoop);
  socket.on("room_unavailable", stopLoop);

  return () => {
    stopLoop();
    pending.forEach((id) => clearTimeout(id));
    pending.clear();
    try {
      socket.disconnect();
    } catch {
      /* ignore */
    }
  };
}
